import { type ReactNode, type RefObject, useRef } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { ChevronsLeftIcon, ChevronsRightIcon } from "lucide-react";

import ErrorBoundary from "simplydo/react/components/Error/ErrorBoundary";
import { cx, styled } from "simplydo/react/styled";
import {
  belowComputer,
  computerUp,
  largeComputerUp,
  tabletUp,
} from "simplydo/react/theme";
import { useTheme } from "simplydo/react/theme";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Container } from "simplydo/react/ui/container";

import { NAV_BAR_HEIGHT, Sticky } from "components/lib/UI";

import SideNavLink, {
  COLLAPSED_LINK_SIZE,
  type SideNavLinkProps,
} from "./SideNavLink";

// Vertical padding of the sidenav layout on computer (horizontal padding comes
// from the shared page `Container`). The sticky sidebar is offset by this too
// so it is already in its stuck position at scroll 0.
const LAYOUT_PADDING = 32;

// Horizontal padding of the scrolling sticky sidebar (room for the scrollbar).
const STICKY_PADDING_LEFT = 2;
const STICKY_PADDING_RIGHT = 8;
// Marks a full-width sidenav page; the app layout drops its bottom padding.
export const SIDENAV_FULL_WIDTH_CLASS = "sidenav-full-width";

const COLLAPSED_WIDTH =
  COLLAPSED_LINK_SIZE + STICKY_PADDING_LEFT + STICKY_PADDING_RIGHT;

const StyledContainer = styled.div`
  flex: 1;
  display: flex;

  ${belowComputer} {
    flex-direction: column;
  }

  ${computerUp} {
    flex-direction: row;

    &.has-main {
      gap: 1rem;
      padding: ${LAYOUT_PADDING}px 0;
    }
  }
`;

const StyledSideBar = styled.div`
  flex: 1;
  max-width: 100%;
  background-color: transparent;

  ${belowComputer} {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 10px;
    padding: 10px;
  }

  ${computerUp} {
    &.has-main {
      flex: 0 0 250px;
      min-height: 80vh;
      max-width: 250px;

      &.is-collapsed {
        flex: 0 0 ${COLLAPSED_WIDTH}px;
        max-width: ${COLLAPSED_WIDTH}px;
      }
    }
  }
`;

const StyledMain = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 100%;
  min-width: 0;

  ${belowComputer} {
    padding: 10px;
  }

  ${computerUp} {
    flex: 1 1 15%;
  }
`;

const OuterContainer = styled.div`
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
`;

type SideNavCustomProps = { content: ReactNode };

export type SideNavComponentCollapsibleProps = {
  // Controlled collapsible
  // Custom nav items are not displayed in collapsible side nav links
  collapsible: boolean;
  isCollapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
};

export type SideNavItemProps = { key: string } & (
  | ({ type: "link" } & SideNavLinkProps)
  | ({ type: "custom" } & SideNavCustomProps)
) &
  Partial<SideNavComponentCollapsibleProps>;

type SideNavComponentProps = {
  nav?: ReactNode;
  navItems?: (SideNavItemProps | null)[];
  main: ReactNode;
  /** Combined height of the sticky bars above the sidenav (navbar, toolbars). */
  top?: number;
  loading?: boolean;
  afterContent?: ReactNode;
  beforeContent?: ReactNode;
  /** Skip scrolling the page to the sidenav top on link click (e.g. in-page section nav). */
  disableScrollToTop?: boolean;
  /**
   * Element used as the scroll landmark on nav click. When the page has
   * scrolled past it, clicks snap so its bottom sits under the sticky offset.
   * Pass the entity header when it lives inside `main` — that restores the
   * old “land below the banner” behaviour from when the header sat above the
   * sidenav. Defaults to the sidenav container top.
   */
  scrollTargetRef?: RefObject<HTMLElement | null>;
  /** Drop the centred max-width (keeping side padding) and the app's bottom padding, e.g. the project board. */
  fullWidth?: boolean;
} & Partial<SideNavComponentCollapsibleProps>;

const StyledSticky = styled(Sticky)<{ top: number }>`
  ${belowComputer} {
    padding-top: 10px;
    padding-bottom: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
  }

  ${computerUp} {
    &.is-scrolling {
      max-height: calc(100vh - ${({ top }) => top}px);
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;
      padding-bottom: 15px;
      padding-right: ${STICKY_PADDING_RIGHT}px;
      padding-left: ${STICKY_PADDING_LEFT}px;
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0);

      &:hover {
        scrollbar-color: rgba(0, 0, 0, 0.35) rgba(0, 0, 0, 0);
      }
    }
  }
`;

type SideNavContainerProps = {
  showMobileView: boolean;
  children: ReactNode;
  top: number;
  scrolling: boolean;
};

const SideNavContainer = ({
  showMobileView,
  children,
  top,
  scrolling,
}: SideNavContainerProps) => {
  if (showMobileView) {
    return (
      <Accordion styled fluid>
        <AccordionTitle index={0}>Navigation</AccordionTitle>
        <AccordionContent index={0}>{children}</AccordionContent>
      </Accordion>
    );
  }
  return (
    <StyledSticky
      top={scrolling ? top + LAYOUT_PADDING : top}
      bottom={undefined}
      className={cx(scrolling && "is-scrolling")}
    >
      {children}
    </StyledSticky>
  );
};

const SideNav = ({
  nav,
  navItems,
  main,
  top,
  loading,
  collapsible,
  isCollapsed,
  onCollapse,
  onExpand,
  afterContent,
  beforeContent,
  disableScrollToTop,
  scrollTargetRef,
  fullWidth,
}: SideNavComponentProps) => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation();
  // Remove empty nav items
  const filteredNavItems = navItems
    ? navItems.filter((item) => item !== null)
    : null;

  const containerRef = useRef<HTMLDivElement>(null);
  const topOffset = top ?? NAV_BAR_HEIGHT;

  const shouldShowCollapsed = isCollapsed && !theme.sizes.isMobile;
  // On computer share the centred page container used by e.g. /challenges and
  // /groups, so very wide screens get extra side space before the 2rem padding.
  const LayoutContainer =
    theme.sizes.isComputer && !!main ? Container : OuterContainer;
  return (
    <LayoutContainer
      className={cx(fullWidth && SIDENAV_FULL_WIDTH_CLASS)}
      style={{
        flex: 1,
        position: "relative",
        ...(fullWidth ? { maxWidth: "none" } : {}),
      }}
    >
      <StyledContainer ref={containerRef} className={cx(!!main && "has-main")}>
        {!filteredNavItems?.length &&
        !nav &&
        !beforeContent &&
        !afterContent ? null : (
          <StyledSideBar
            className={cx(
              shouldShowCollapsed && "is-collapsed",
              !!main && "has-main",
              !navItems?.length && "is-empty",
            )}
          >
            <SideNavContainer
              showMobileView={!theme.sizes.isComputer && !!main}
              top={topOffset}
              scrolling={!!main}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {beforeContent ? beforeContent : null}
                {nav ? nav : null}
                {(filteredNavItems ?? []).map((item) =>
                  item.type === "custom" ? (
                    item.content
                  ) : (
                    <SideNavLink
                      {...item}
                      onClick={() => {
                        item?.onClick?.();
                        if (disableScrollToTop) {
                          return;
                        }
                        const headerTarget = scrollTargetRef?.current;
                        const bounds = (
                          headerTarget ?? containerRef.current
                        )?.getBoundingClientRect();
                        // Header-in-main: land just below the header (same as
                        // when the header sat above the sidenav). Otherwise
                        // land at the container top.
                        const anchorY = headerTarget
                          ? bounds?.bottom
                          : bounds?.top;

                        if (
                          bounds &&
                          anchorY !== undefined &&
                          anchorY - topOffset < 0
                        ) {
                          document.body.parentElement?.scrollTo({
                            top:
                              document.body.parentElement.scrollTop +
                              anchorY -
                              topOffset -
                              10,
                            behavior: "instant",
                          });
                        }
                      }}
                      key={item.key}
                      collapsible={
                        theme.sizes.isComputer ? !!collapsible : false
                      }
                      isCollapsed={isCollapsed}
                      onCollapse={onCollapse}
                      onExpand={onExpand}
                    />
                  ),
                )}
                {collapsible &&
                onCollapse &&
                onExpand &&
                theme.sizes.isComputer &&
                !loading ? (
                  <div style={{ marginTop: 15 }}>
                    <SideNavLink
                      key="collapse"
                      onClick={isCollapsed ? onExpand : onCollapse}
                      content={
                        isCollapsed
                          ? t("sidebar.expand")
                          : t("sidebar.collapse")
                      }
                      isCollapsed={isCollapsed}
                      iconLeft={
                        isCollapsed ? ChevronsRightIcon : ChevronsLeftIcon
                      }
                      active={false}
                      secondary
                      compact="very"
                    />
                  </div>
                ) : null}
                {afterContent ? afterContent : null}
              </div>
            </SideNavContainer>
          </StyledSideBar>
        )}
        {main ? (
          <ErrorBoundary
            padding="0px 0px 0px 10px"
            pathName={location.pathname}
          >
            <StyledMain>{main}</StyledMain>
          </ErrorBoundary>
        ) : null}
      </StyledContainer>
    </LayoutContainer>
  );
};

export default SideNav;
